/** Подсветка бренда, который уже искали. Сбрасывается через 24 часа. */
export const VISITED_BRAND_SUGGESTIONS_KEY = 'at.ug.visited-brand-suggestions';

export const VISITED_BRAND_SUGGESTION_TTL_MS = 24 * 60 * 60 * 1000;

export type VisitedBrandSuggestionsMap = Record<string, number>;

const listeners = new Set<() => void>();

let expiryTimer: number | undefined;

function notifyVisitedBrandSuggestions() {
    listeners.forEach((listener) => listener());
}

export function subscribeVisitedBrandSuggestions(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

export function getVisitedBrandSuggestionKey(article: string, brand: string): string {
    return `${article.trim().toLowerCase()}\u0000${brand.trim().toLowerCase()}`;
}

function isFreshTimestamp(timestamp: unknown, now: number): timestamp is number {
    return (
        typeof timestamp === 'number' &&
        Number.isFinite(timestamp) &&
        now - timestamp < VISITED_BRAND_SUGGESTION_TTL_MS
    );
}

function writeVisitedBrandSuggestions(map: VisitedBrandSuggestionsMap) {
    try {
        if (Object.keys(map).length === 0) {
            window.localStorage.removeItem(VISITED_BRAND_SUGGESTIONS_KEY);
            return;
        }

        window.localStorage.setItem(VISITED_BRAND_SUGGESTIONS_KEY, JSON.stringify(map));
    } catch {
        // ignore quota / private mode
    }
}

export function readVisitedBrandSuggestions(): VisitedBrandSuggestionsMap {
    if (typeof window === 'undefined') {
        return {};
    }

    try {
        const raw = window.localStorage.getItem(VISITED_BRAND_SUGGESTIONS_KEY);

        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw) as unknown;

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }

        const now = Date.now();
        const fresh: VisitedBrandSuggestionsMap = {};
        let expired = false;

        for (const [key, timestamp] of Object.entries(parsed)) {
            if (isFreshTimestamp(timestamp, now)) {
                fresh[key] = timestamp;
            } else {
                expired = true;
            }
        }

        if (expired) {
            writeVisitedBrandSuggestions(fresh);
        }

        return fresh;
    } catch {
        return {};
    }
}

export function scheduleVisitedBrandSuggestionsExpiry(
    map: VisitedBrandSuggestionsMap = readVisitedBrandSuggestions()
) {
    if (typeof window === 'undefined') {
        return;
    }

    if (expiryTimer !== undefined) {
        window.clearTimeout(expiryTimer);
        expiryTimer = undefined;
    }

    const now = Date.now();
    let nearestExpiry = Infinity;

    for (const timestamp of Object.values(map)) {
        const expiresAt = timestamp + VISITED_BRAND_SUGGESTION_TTL_MS;

        if (expiresAt < nearestExpiry) {
            nearestExpiry = expiresAt;
        }
    }

    if (!Number.isFinite(nearestExpiry)) {
        return;
    }

    const maxTimeoutMs = 2_147_483_647;
    const delay = Math.min(maxTimeoutMs, Math.max(0, nearestExpiry - now));

    expiryTimer = window.setTimeout(() => {
        expiryTimer = undefined;
        const fresh = readVisitedBrandSuggestions();
        notifyVisitedBrandSuggestions();
        scheduleVisitedBrandSuggestionsExpiry(fresh);
    }, delay);
}

export function markVisitedBrandSuggestion(article: string, brand: string) {
    if (!article.trim() || !brand.trim()) {
        return;
    }

    const map = readVisitedBrandSuggestions();
    map[getVisitedBrandSuggestionKey(article, brand)] = Date.now();
    writeVisitedBrandSuggestions(map);
    scheduleVisitedBrandSuggestionsExpiry(map);
    notifyVisitedBrandSuggestions();
}

export function isVisitedBrandSuggestion(
    map: VisitedBrandSuggestionsMap,
    article: string,
    brand: string
): boolean {
    const timestamp = map[getVisitedBrandSuggestionKey(article, brand)];

    return isFreshTimestamp(timestamp, Date.now());
}
